/**
 * ProjectConfigPanel — project settings tab: details, resume upload, criteria selection.
 * Enhanced with CC Emails and Calendar Invitees sections.
 */
import { withFeedback } from '../lib/buttonFeedback.js';

export class ProjectConfigPanel {
    constructor(options = {}) {
        this._projectStore = options.projectStore;
        this._promptStore = options.promptStore;
        this._candidateStore = options.candidateStore;
        this._analysisQueue = options.analysisQueue;
        this._statusBar = options.statusBar;
        this._onPromptsChanged = options.onPromptsChanged || (() => {});
        this._onImportComplete = options.onImportComplete || (() => {});
        this._projectId = null;
        this._pendingFiles = [];
        this._el = document.createElement('div');
        this._el.className = 'rm-config-panel';
    }

    get element() { return this._el; }

    setProject(projectId) {
        this._projectId = projectId;
        this._pendingFiles = [];
        this.render();
    }

    render() {
        this._el.innerHTML = '';
        const project = this._projectStore.getById(this._projectId);
        if (!project) {
            this._el.innerHTML = '<div class="rm-empty-state"><div>Select a project first</div></div>';
            return;
        }

        const allPrompts = this._promptStore.getAll();
        const selected = new Set(project.selectedPrompts || []);

        // --- Project info section ---
        const infoSection = document.createElement('div');
        infoSection.className = 'rm-config-section';
        infoSection.innerHTML = `
            <h3>Project Details</h3>
            <div class="rm-form-row" style="margin-bottom:12px;">
                <label style="font-size:12px;color:var(--text-muted);font-weight:600;">Name</label>
                <input type="text" id="rmConfigName" value="${this._esc(project.name)}"
                    style="background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;">
            </div>
            <div class="rm-form-row" style="margin-bottom:12px;">
                <label style="font-size:12px;color:var(--text-muted);font-weight:600;">Description</label>
                <input type="text" id="rmConfigDesc" value="${this._esc(project.description || '')}"
                    style="background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;">
            </div>
            ${project.campaignEmail ? `
            <div class="rm-form-row" style="margin-bottom:12px;">
                <label style="font-size:12px;color:var(--text-muted);font-weight:600;">Campaign Email</label>
                <div style="display:flex;align-items:center;gap:8px;">
                    <input type="text" readonly value="${this._esc(project.campaignEmail)}" id="rmCampaignEmail"
                        style="background:var(--bg-tertiary);color:var(--primary);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;flex:1;cursor:text;font-family:monospace;">
                    <button class="rm-btn" id="rmCopyEmail" style="padding:8px 12px;" title="Copy to clipboard">Copy</button>
                </div>
                <div style="font-size:11px;color:var(--text-muted);margin-top:4px;">
                    Outbound emails to candidates come from this address. Forward resumes to this address to add them to this campaign automatically.
                </div>
            </div>` : ''}
            <button class="rm-btn rm-btn-primary" id="rmConfigSaveInfo">Save</button>
        `;
        const copyBtn = infoSection.querySelector('#rmCopyEmail');
        if (copyBtn) {
            copyBtn.addEventListener('click', () => {
                const emailInput = infoSection.querySelector('#rmCampaignEmail');
                navigator.clipboard.writeText(emailInput.value).then(() => {
                    copyBtn.textContent = 'Copied!';
                    setTimeout(() => { copyBtn.textContent = 'Copy'; }, 2000);
                });
            });
        }
        const saveInfoBtn = infoSection.querySelector('#rmConfigSaveInfo');
        saveInfoBtn.addEventListener('click', () => withFeedback(saveInfoBtn, async () => {
            await this._projectStore.update(this._projectId, {
                name: infoSection.querySelector('#rmConfigName').value.trim(),
                description: infoSection.querySelector('#rmConfigDesc').value.trim(),
            });
            if (this._statusBar) this._statusBar.log('Project details saved', 'success');
        }, { successText: '\u2713 Saved' }));
        this._el.appendChild(infoSection);

        // --- CC Emails section ---
        const ccSection = document.createElement('div');
        ccSection.className = 'rm-config-section';
        ccSection.innerHTML = `
            <h3>CC on All Emails</h3>
            <div style="font-size:12px;color:var(--text-muted);margin-bottom:8px;">
                These email addresses will be CC'd on all candidate communications sent from this project. One email per line.
            </div>
            <div class="rm-form-row" style="margin-bottom:12px;">
                <textarea id="rmConfigCcEmails" rows="4" placeholder="manager@example.com&#10;hr@example.com"
                    style="background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;font-family:inherit;resize:vertical;min-height:80px;">${this._esc((project.ccEmails || []).join('\n'))}</textarea>
            </div>
            <button class="rm-btn rm-btn-primary" id="rmConfigSaveCc">Save CC Emails</button>
        `;
        const saveCcBtn = ccSection.querySelector('#rmConfigSaveCc');
        saveCcBtn.addEventListener('click', () => withFeedback(saveCcBtn, async () => {
            const raw = ccSection.querySelector('#rmConfigCcEmails').value.trim();
            const ccEmails = raw.split('\n').map(e => e.trim().toLowerCase()).filter(Boolean);
            await this._projectStore.update(this._projectId, { ccEmails });
            if (this._statusBar) this._statusBar.log(`Saved ${ccEmails.length} CC email(s)`, 'success');
        }, { successText: '\u2713 Saved' }));
        this._el.appendChild(ccSection);

        // --- Calendar Invitees section ---
        const calSection = document.createElement('div');
        calSection.className = 'rm-config-section';
        calSection.innerHTML = `
            <h3>Calendar Invitees</h3>
            <div style="font-size:12px;color:var(--text-muted);margin-bottom:8px;">
                These people will be automatically invited to interview calendar events created for this project. One email per line.
            </div>
            <div class="rm-form-row" style="margin-bottom:12px;">
                <textarea id="rmConfigCalInvitees" rows="4" placeholder="interviewer1@example.com&#10;interviewer2@example.com"
                    style="background:var(--bg-secondary);color:var(--text);border:1px solid var(--border);border-radius:6px;padding:8px 12px;font-size:14px;font-family:inherit;resize:vertical;min-height:80px;">${this._esc((project.calendarInvitees || []).join('\n'))}</textarea>
            </div>
            <button class="rm-btn rm-btn-primary" id="rmConfigSaveCal">Save Calendar Invitees</button>
        `;
        const saveCalBtn = calSection.querySelector('#rmConfigSaveCal');
        saveCalBtn.addEventListener('click', () => withFeedback(saveCalBtn, async () => {
            const raw = calSection.querySelector('#rmConfigCalInvitees').value.trim();
            const calendarInvitees = raw.split('\n').map(e => e.trim().toLowerCase()).filter(Boolean);
            await this._projectStore.update(this._projectId, { calendarInvitees });
            if (this._statusBar) this._statusBar.log(`Saved ${calendarInvitees.length} calendar invitee(s)`, 'success');
        }, { successText: '\u2713 Saved' }));
        this._el.appendChild(calSection);

        // --- Resume upload section ---
        const uploadSection = document.createElement('div');
        uploadSection.className = 'rm-config-section';
        uploadSection.innerHTML = `
            <h3>Import Resumes</h3>
            <div class="rm-import-dropzone" id="rmConfigDropzone">
                <div style="font-size:24px;margin-bottom:4px;">📄</div>
                <div>Drop PDF files here or click to browse</div>
                <div style="font-size:11px;margin-top:4px;color:var(--text-muted);">Accepts .pdf files only</div>
                <input type="file" id="rmConfigFileInput" accept=".pdf" multiple style="display:none;">
            </div>
            <div id="rmConfigFileList" class="rm-import-file-list"></div>
            <div id="rmConfigUploadProgress" style="display:none;">
                <div style="font-size:13px;color:var(--text-muted);margin-bottom:4px;" id="rmConfigProgressText">Uploading...</div>
                <div class="rm-progress"><div class="rm-progress-bar" id="rmConfigProgressBar" style="width:0%"></div></div>
            </div>
            <div style="margin-top:12px;">
                <button class="rm-btn rm-btn-primary" id="rmConfigUploadBtn" disabled>Upload 0 files</button>
            </div>
        `;

        const dropzone = uploadSection.querySelector('#rmConfigDropzone');
        const fileInput = uploadSection.querySelector('#rmConfigFileInput');

        dropzone.addEventListener('dragover', (e) => { e.preventDefault(); dropzone.classList.add('dragover'); });
        dropzone.addEventListener('dragleave', () => dropzone.classList.remove('dragover'));
        dropzone.addEventListener('drop', (e) => {
            e.preventDefault();
            dropzone.classList.remove('dragover');
            this._addFiles(e.dataTransfer.files, uploadSection);
        });
        dropzone.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', () => this._addFiles(fileInput.files, uploadSection));
        uploadSection.querySelector('#rmConfigUploadBtn').addEventListener('click', () => this._upload(uploadSection));

        this._el.appendChild(uploadSection);

        // --- Criteria selection section ---
        const criteriaSection = document.createElement('div');
        criteriaSection.className = 'rm-config-section';
        criteriaSection.innerHTML = '<h3>Active Evaluation Criteria</h3>';

        if (allPrompts.length === 0) {
            criteriaSection.innerHTML += `
                <div style="color:var(--text-muted);font-size:13px;padding:12px 0;">
                    No criteria defined yet. Go to the Criteria tab to create some.
                </div>`;
        } else {
            for (const prompt of allPrompts) {
                const item = document.createElement('div');
                item.className = 'rm-config-prompt-item';

                const cb = document.createElement('input');
                cb.type = 'checkbox';
                cb.className = 'rm-checkbox';
                cb.checked = selected.has(prompt.id);
                cb.addEventListener('change', () => this._togglePrompt(prompt.id, cb.checked));

                const nameSpan = document.createElement('span');
                nameSpan.className = 'rm-config-prompt-name';
                nameSpan.textContent = prompt.name;

                const descSpan = document.createElement('span');
                descSpan.className = 'rm-config-prompt-desc';
                descSpan.textContent = prompt.description || '';

                item.appendChild(cb);
                item.appendChild(nameSpan);
                item.appendChild(descSpan);
                criteriaSection.appendChild(item);
            }
        }

        this._el.appendChild(criteriaSection);
    }

    // ========== FILE UPLOAD ==========

    _addFiles(fileList, section) {
        for (const f of fileList) {
            if (f.type === 'application/pdf' && !this._pendingFiles.some(x => x.name === f.name)) {
                this._pendingFiles.push(f);
            }
        }
        this._renderFileList(section);
    }

    _renderFileList(section) {
        const list = section.querySelector('#rmConfigFileList');
        const btn = section.querySelector('#rmConfigUploadBtn');

        list.innerHTML = this._pendingFiles.map((f, i) => `
            <div class="rm-import-file-item">
                <span>${this._esc(f.name)} (${(f.size / 1024).toFixed(0)} KB)</span>
                <button class="rm-import-file-remove" data-idx="${i}">&times;</button>
            </div>
        `).join('');

        for (const removeBtn of list.querySelectorAll('.rm-import-file-remove')) {
            removeBtn.addEventListener('click', () => {
                this._pendingFiles.splice(parseInt(removeBtn.dataset.idx), 1);
                this._renderFileList(section);
            });
        }

        btn.textContent = `Upload ${this._pendingFiles.length} file${this._pendingFiles.length !== 1 ? 's' : ''}`;
        btn.disabled = this._pendingFiles.length === 0;
    }

    async _upload(section) {
        if (!this._projectId || this._pendingFiles.length === 0) return;

        const progress = section.querySelector('#rmConfigUploadProgress');
        const bar = section.querySelector('#rmConfigProgressBar');
        const text = section.querySelector('#rmConfigProgressText');
        const btn = section.querySelector('#rmConfigUploadBtn');

        progress.style.display = 'block';
        btn.disabled = true;

        const storage = firebase.storage();
        const total = this._pendingFiles.length;
        let uploaded = 0;

        for (const file of this._pendingFiles) {
            text.textContent = `Uploading ${uploaded + 1}/${total}: ${file.name}`;
            bar.style.width = `${(uploaded / total) * 100}%`;

            try {
                const storagePath = `resumes/${this._projectId}/${Date.now()}_${file.name}`;
                const ref = storage.ref(storagePath);
                await ref.put(file, { contentType: 'application/pdf' });
                await this._candidateStore.create(this._projectId, file.name, storagePath);
                uploaded++;
                if (this._statusBar) this._statusBar.log(`Uploaded: ${file.name}`, 'success');
            } catch (err) {
                console.error('Upload error:', err);
                if (this._statusBar) this._statusBar.log(`Failed: ${file.name} — ${err.message}`, 'error');
            }
        }

        bar.style.width = '100%';
        text.textContent = `Done — ${uploaded}/${total} uploaded. Starting extraction...`;
        this._pendingFiles = [];
        this._onImportComplete(uploaded);

        // Auto-trigger extraction for newly uploaded candidates
        if (uploaded > 0 && this._analysisQueue) {
            try {
                const candidates = this._candidateStore.getAll();
                const count = await this._analysisQueue.createExtractionJob(candidates, this._projectId);
                if (count > 0 && this._statusBar) {
                    this._statusBar.log(`Auto-started extraction for ${count} candidate(s)`, 'success');
                }
                text.textContent = `Done — ${uploaded} uploaded, extraction started`;
            } catch (err) {
                console.error('Auto-extraction failed:', err);
                if (this._statusBar) this._statusBar.log(`Auto-extraction failed: ${err.message}`, 'error');
            }
        }

        setTimeout(() => {
            progress.style.display = 'none';
            btn.textContent = 'Upload 0 files';
            btn.disabled = true;
            section.querySelector('#rmConfigFileList').innerHTML = '';
        }, 2000);
    }

    // ========== CRITERIA ==========

    async _togglePrompt(promptId, enabled) {
        const project = this._projectStore.getById(this._projectId);
        if (!project) return;

        let selected = [...(project.selectedPrompts || [])];
        if (enabled && !selected.includes(promptId)) {
            selected.push(promptId);
        } else if (!enabled) {
            selected = selected.filter(id => id !== promptId);
        }

        await this._projectStore.update(this._projectId, { selectedPrompts: selected });
        this._onPromptsChanged(selected);
        if (this._statusBar) this._statusBar.log(`Criteria updated`, 'info');
    }

    _esc(str) {
        const d = document.createElement('div');
        d.textContent = str || '';
        return d.innerHTML;
    }
}
